import { ReactElement } from 'react';
import {
    Edit,
    SimpleForm,
    SelectInput,
    ReferenceArrayInput,
    useGetList,
    TextInput,
    TextArrayInput,
    SelectArrayInput,
    ImageInput,
    required,
    ImageField,
    ReferenceInput,
    FormDataConsumer,
    FormDataConsumerRenderParams,
    useChoicesContext,
    AutocompleteArrayInput
} from 'react-admin';
import { nokSoloOrCompanyOptions, nokStatus, smtpZones } from "../../../../defaults/defaults";
import { NokEmailPreview } from "./NokEmailPreview";
import { SenderDescriptionInput } from "./SenderDescriptionInput";
import {
    Grid,
    Box, Button, CircularProgress
} from '@mui/material'
import { ChatFieldBridge } from "../../../chat-bot/components/ChatFieldBridge";

const STEP = 50; // how many extra records to pull in per click

const LoadMoreAutocomplete = (props) => {
    const { perPage, setPerPage, isFetching, hasNextPage } = useChoicesContext();

    return (
        <Box>
            <AutocompleteArrayInput {...props} />
            {hasNextPage && (
                <Button
                    size="small"
                    disabled={isFetching}
                    onClick={() => setPerPage(perPage + STEP)}
                >
                    {isFetching ? <CircularProgress size={16} /> : 'Load more'}
                </Button>
            )}
        </Box>
    );
};

const LoadMoreSelect = (props) => {
    const { perPage, setPerPage, isFetching, hasNextPage } = useChoicesContext();

    return (
        <Box>
            <SelectInput {...props} />
            {hasNextPage && (
                <Button
                    size="small"
                    disabled={isFetching}
                    onClick={() => setPerPage(perPage + STEP)}
                >
                    {isFetching ? <CircularProgress size={16} /> : 'Load more'}
                </Button>
            )}
        </Box>
    );
};

export const NokEditForm = () => {

    const { data, total, isPending, error } = useGetList(
        'industries',
        {
            pagination: { page: 1, perPage: 500 },
        }
    );
    let industries: { id: string, name: string }[] = [];
    if (data) {
        for (const item of data) {
            industries.push({
                id: item.id,
                name: item.name
            });
        }
    }

    // Numbers for the website's own industries, which HQ-API links by id (`nokwebsiteDataProvider`).
    const industryIds: { id: number, name: string }[] = industries.map( (item: { id: string, name: string }): { id: number, name: string } => ({ id: Number(item.id), name: item.name }) );

    if (isPending) {
        return null;
    }

    return (
        <SimpleForm  >
            <Grid container spacing={2}>
                <Grid size={6}>
                    <TextInput source="name" validate={ [required()] } />
                    <SelectInput source="status" choices={ nokStatus } defaultValue="active" validate={ [required()] } />
                    <TextInput source="description" multiline />
                    <SelectArrayInput source="targetIndustries" choices={ industries } />
                    <AutocompleteArrayInput source="industries"
                                            label="Website industries"
                                            choices={ industryIds }
                                            defaultValue={ [] }
                                            sx={ { minWidth: 300 } }
                                            helperText="The industries this website's own business is in. Target industries are the ones it shops." />
                    <SelectInput source="soloOrCompany"
                                 choices={ nokSoloOrCompanyOptions }
                                 validate={ [required()] } />
                    <SenderDescriptionInput />
                    <ReferenceArrayInput source="nokPersonas"
                                         reference="nok_personas"
                                         perPage={25}
                                          >
                        <LoadMoreAutocomplete validate={ [required()] } />
                    </ReferenceArrayInput>
                    <FormDataConsumer<{ soloOrCompany: string }>>
                        { ( { formData, ...rest }: FormDataConsumerRenderParams<{ soloOrCompany: string }, { soloOrCompany: string }> ): ReactElement => {
                            const helper: string = "This domain needs to be set in MailGun for emails to work. Not needed for solo if not sending via Mailgun. Needed if sending via Mailgun and solo";
                            if (formData.soloOrCompany === 'company') {
                                return (
                                    <TextInput source="url"
                                               validate={ [required()] }
                                               helperText={ helper } />
                                );
                            }

                            return (
                                <TextInput source="url"
                                           helperText={ helper } />
                            );
                        } }
                    </FormDataConsumer>

                    <SelectInput source="smtpServerZone"
                                 choices={ smtpZones }
                                 helperText="This value must match the MailGun sending server for the domain or will fail to send!"
                                 validate={ [required()] } />
                    <ImageInput source="logo"
                                label="Company Logo"
                                helperText="This image needs to be on the Nok site server to render in email footer" >
                        <ImageField source="src" title="title" />
                    </ImageInput>
                    <TextInput source="productionLogoPath" helperText={ "This value is needed if the signature template has the Nok logo" }/>
                </Grid>
                <Grid size={6}>
                    <FormDataConsumer<{ soloOrCompany: string }>>
                        { ({formData, ...rest}: FormDataConsumerRenderParams<{ soloOrCompany: string }, { soloOrCompany: string }> ): ReactElement => {

                            if (formData.soloOrCompany === 'company') {
                                return (
                                    <>
                                        <ReferenceInput source="nokEmailTemplate"
                                                        reference="nok_email_templates"
                                                        { ...rest }
                                        >
                                            <SelectInput  validate={ [required()] } />
                                        </ReferenceInput>
                                        <ReferenceInput source="nokEmailSignature"
                                                        reference="nok_email_signatures"
                                                        { ...rest }
                                        >
                                            <LoadMoreSelect  validate={ [required()] } />
                                        </ReferenceInput>
                                    </>
                                );
                            }

                            return (
                                <>
                                    <ReferenceInput source="nokEmailTemplate"
                                                    reference="nok_email_templates"
                                                    { ...rest }
                                    />
                                    <ReferenceInput source="nokEmailSignature"
                                                    reference="nok_email_signatures"
                                                    { ...rest }
                                    />
                                </>
                            );
                        } }
                    </FormDataConsumer>
                    <NokEmailPreview />
                </Grid>
            </Grid>
            {/* Offers this form to the assistant. Renders nothing, writes nothing by itself. */}
            <ChatFieldBridge />
        </SimpleForm>
    );
}